function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}

class RiderTagEditor {
	logPerformance(methodName, startTime) {
		const duration = performance.now() - startTime;
		console.log(`${methodName} executed in ${duration.toFixed(2)}ms`);
	}

	constructor(containerId, options = {}) {
		// Initialize tracking maps and state first
		this.boundHandlers = new Map();
		this.preloadedFlags = new Map();
		this.flagLoadingPromises = new Map();
		this.isDestroyed = false;
		this.flagObserver = null;

		// Initialize DOM references
		this.containerId = containerId;
		this.container = document.getElementById(containerId);
		this.outerContainer = document.querySelector(`.product section`);
		this.canvasId = `canvas-${containerId}`;
		this.flagPlaceholderId = `flag-placeholder-${containerId}`;
		this.fontSelectId = `font-select-${containerId}`;
		this.themeSelectId = `theme-select-${containerId}`;
		this.nameInput = document.getElementById(`name-input-${containerId}`);
		this.pngHashInput = this.container.querySelector(
			'input[name="properties[_Png]"]'
		);
		this.flagUrlInput = this.container.querySelector(
			'input[name="properties[_Icon]"]'
		);
		this.fontInput = this.container.querySelector(
			'input[name="properties[_Font]"]'
		);
		this.themeInput = this.container.querySelector(
			'input[name="properties[_Theme]"]'
		);
        this.textColorInput = this.container.querySelector(
			'input[name="properties[_TextColor]"]'
		);
        this.backgroundColorInput = this.container.querySelector(
			'input[name="properties[_BackgroundColor]"]'
		);

		this.options = {
			fonts: [
              
			],
			canvasWidth: 660,
			flagWidth: 120,
			flagHeight: 90,
			flagX: 20,
			flagY: 20,
			margin: 20,
			backgroundColor: "black",
			textColor: "white",
			preloadedFlag: "Sweden",
			colors: [
				// Row 1
				"#000000", "#663300", "#CC6600", "#660066", "#660099", "#000066", "#006666", "#006600",
				// Row 2
				"#666666", "#990000", "#FF9900", "#CC0000", "#CC0099", "#0000CC", "#009999", "#336600",
				// Row 3
				"#999999", "#663300", "#FFCC00", "#FF6600", "#FF66CC", "#0066FF", "#00CCCC", "#00CC00",
				// Row 4
				"#CCCCCC", "#996633", "#FFFF00", "#FF9999", "#FF99FF", "#3399FF", "#66FFCC", "#66CC00",
				// Row 5
				"#FFFFFF", "#CC9966", "#FFFF99", "#FFCCCC", "#FF99FF", "#99CCFF", "#99FFCC", "#99FF00"
			],
			...options,
		};
		console.log(this.options);
		
		// Add loading state
		this.outerContainer.style.setProperty("--editor-opacity", "0");
		this.outerContainer.classList.add('is-loading');
		
		// Initialize basic elements
		this.initializeCanvas();
		this.initializeAssetUrls();
		this.initializeElements();
		this.setupEventListeners();

		// Initialize the editor
		this.initialize().then(() => {
			this.outerContainer.classList.remove('is-loading');
			this.outerContainer.style.setProperty("--editor-opacity", "1");
		});
	}

	destroy() {
		if (this.isDestroyed) return;
		this.isDestroyed = true;

		// Clean up IntersectionObserver
		if (this.flagObserver) {
			this.flagObserver.disconnect();
			this.flagObserver = null;
		}

		// Clean up preloaded flags
		for (const [flagCode, img] of this.preloadedFlags.entries()) {
			if (img.src.startsWith('blob:')) {
				URL.revokeObjectURL(img.src);
			}
			this.preloadedFlags.delete(flagCode);
		}

		// Clean up loading promises
		this.flagLoadingPromises.clear();

		// Clean up canvas
		if (this.canvas) {
			this.canvas.dispose();
			this.canvas = null;
		}

		// Clean up event listeners
		window.removeEventListener('resize', this.boundHandlers.get('resize'));
		this.boundHandlers.clear();

		// Clean up DOM references
		this.container = null;
		this.outerContainer = null;
		this.nameInput = null;
		this.pngHashInput = null;
		this.flagUrlInput = null;
		this.fontInput = null;
		this.themeInput = null;
		this.textColorInput = null;
		this.backgroundColorInput = null;
		this.originalSelects = null;
		this.customSelects = null;
	}

	async initialize() {
		const startTime = performance.now();
		console.log('Initializing editor...');
		try {
			console.log(this.options.preloadedFlag);
			// Initialize all selects first
			await this.initializeSelects();

			// Load initial font
			await this.loadInitialFont();

			// Load and setup flags
			await this.loadCountryFlags();

			// Setup initial flag if provided and store the flag URL
			if (this.options.preloadedFlag || this.options.shopifyCountry) {
				const flagUrl = await this.setupInitialFlag(
					this.options.shopifyCountry
				);
				if (flagUrl) {
					this.loadedFlagUrl = flagUrl; // Store the loaded flag URL
				}
				this.outerContainer.style.setProperty("--editor-opacity", "1");
			}

			// Set initial mode and resize
			const initialMode = this.determineMode();
			this.setFlagMode(initialMode);
			this.resizeCanvas();
  
			// Force a final render
			this.canvas.renderAll();
            
            // Setup efficient rendering using requestAnimationFrame
            let lastRenderTime = 0;
            const minRenderInterval = 500; // Minimum time between renders in ms

            const scheduleRender = (timestamp) => {
                if (!lastRenderTime || timestamp - lastRenderTime >= minRenderInterval) {
                    this.canvas.requestRenderAll();
                    lastRenderTime = timestamp;
                }
                if (!this.isDestroyed) {
                    requestAnimationFrame(scheduleRender);
                }
            };
            
            requestAnimationFrame(scheduleRender);
			this.logPerformance('initialize', startTime);
		} catch (error) {
			console.error("Error initializing editor:", error);
		}
	}

	async initializeSelects() {
		// Store references to original selects
		this.originalSelects = {
			font: document.getElementById(this.fontSelectId),
			theme: document.getElementById(this.themeSelectId),
		};

		// Initialize theme options
		if (this.originalSelects.theme) {
			this.themeOptions = Array.from(this.originalSelects.theme.options).map(
				(option) => ({
					value: option.value,
					label: option.textContent,
					settings: {
						rect: { fill: option.dataset.rectFill || "transparent" },
						text: { fill: option.dataset.textFill || "black" },
						dataset: option.dataset.theme || "",
					},
				})
			);
		}

		// Initialize custom selects object
		this.customSelects = {};

		// Convert font select to custom select and initialize font input
		if (this.originalSelects.font) {
			const initialFont = this.originalSelects.font.value;
       

			if (this.fontInput) {
				this.fontInput.value = initialFont;
			}
            const fontNodes = this.originalSelects.font.querySelectorAll('option');
            const offsetMap = {};
            for (var i = 0; i < fontNodes.length; ++i) {
                offsetMap[fontNodes[i].value] = fontNodes[i].dataset.offset || 0;
            }
            
			this.customSelects.font = this.createCustomSelect(
				this.originalSelects.font,
				{
					onChange: (value) => {
                        
						this.loadAndUse(value, offsetMap[value]);
					},
					customClass: "font-select",
				}
			);
		}

		// Convert theme select to custom select and initialize theme input
		if (this.originalSelects.theme) {
			// Add custom color options to theme select
			const customOption = document.createElement('option');
			customOption.value = 'custom';
			customOption.textContent = this.options.translations["theme-custom"];
			customOption.dataset.theme = 'custom';
			this.originalSelects.theme.appendChild(customOption);

			const initialTheme = this.originalSelects.theme.value;
			if (this.themeInput) {
				this.themeInput.value = initialTheme;
			}

			this.customSelects.theme = this.createCustomSelect(
				this.originalSelects.theme,
				{
					onChange: (value) => {
						if (value === 'custom') {
							this.showCustomColorPicker();
						} else {
							this.resetThemeDefaults();
							this.applyTheme(value);
							this.canvas.requestRenderAll();
						}
					},
					customClass: "theme-select",
				}
			);

			// Apply initial theme
			if (initialTheme) {
				this.applyTheme(initialTheme);
			}
		}

		// Initialize color inputs with default values if they exist
		if (this.textColorInput) {
			this.textColorInput.value = this.options.textColor || "white";
		}
		if (this.backgroundColorInput) {
			this.backgroundColorInput.value = this.options.backgroundColor || "black";
		}

		// Initialize flag input if preloaded flag exists
		if (this.flagUrlInput && this.options.preloadedFlag) {
			const countries = JSON.parse(document.getElementById("editor-icons").textContent);
			const countryCode = await this.findCountryCode(this.options.preloadedFlag, countries);
			if (countryCode) {
				this.flagUrlInput.value = countryCode;
			}
		}
	}

	async loadInitialFont() {
		const startTime = performance.now();
		console.log('Loading initial font...');
		const initialFont =
			this.originalSelects.font || this.options.fonts[0];
		if (!initialFont) return;

            const offset = initialFont.querySelector("option:checked").dataset.offset;
		try {
			// Pre-load all fonts in options.fonts
			await Promise.all(
				this.options.fonts.map(async (font) => {
					if (font === "Times New Roman") return;

					const myfont = new FontFaceObserver(font);
					try {
						await myfont.load(null, 5000);
					} catch (e) {
						console.warn(`Failed to preload font: ${font}`, e);
					}
				})
			);
			// Now load the initial font
			await this.loadAndUse(initialFont.value, offset);
			this.logPerformance('loadInitialFont', startTime);
		} catch (e) {
			console.error("Initial font loading failed:", e);
			// Fallback to system font
			this.riderId.set("fontFamily", "Arial");
			this.syncWidth(this.riderId);
			this.canvas.requestRenderAll();
		}
	}

	async setupInitialFlag() {
		// Determine which flag to use
		const flagToUse = this.options.preloadedFlag || this.options.shopifyCountry;

		if (!flagToUse) {
			console.log("No flag to initialize");
			return;
		}

		// Find the country code for flag from editor-icons.json
		const countries = JSON.parse(
			document.getElementById("editor-icons").textContent
		);

		const countryCode = await this.findCountryCode(flagToUse, countries);

		if (!countryCode) {
			console.warn("No match found for flag:", flagToUse);
			return;
		}

		// Create and load the flag image
		const flagUrl = this.baseAssetUrl + `${countryCode}.svg`;

		// Set the imgObj src and wait for it to load
		return new Promise((resolve) => {
			this.imgObj.onload = () => {
				if (this.leftFlag) {
					fabric.Image.fromURL(
						flagUrl,
						(img) => {
							this.leftFlag.setElement(img.getElement());
							this.leftFlag.set({
								opacity: 1,
								visible: true,
							});
							this.canvas.renderAll();

							resolve(flagUrl); // Resolve with the flag URL
						},
						{ crossOrigin: "anonymous" }
					);
				}
			};
			this.imgObj.src = flagUrl;
		});
	}

	async findCountryCode(flagName, countries) {
		let countryCode = null;

		for (const category of countries.categories) {
			const icon = category.json.icons.find(
				(icon) => icon.name.toLowerCase() === flagName.toLowerCase()
			);
			if (icon) {
				countryCode = icon.code;
				break;
			}
		}

		return countryCode || "de"; // Default to Germany if not found
	}

	waitForElement(selector, callback) {
		const intervalId = setInterval(() => {
			if (document.querySelector(selector)) {
				clearInterval(intervalId);
				callback();
			}
		}, 500);
	}

	createCanvas() {
		const canvas = document.createElement("canvas");
		canvas.id = this.canvasId;
		this.container
			.querySelector(".editor-container")
			.insertBefore(
				canvas,
				this.container.querySelector(".editor-container").firstChild
			);
	}

	initializeAssetUrls() {
		const assetUrl = "//www.ridertags.com/cdn/shop/t/24/assets/test_image.png?9869";
		const assetUrlIndex = assetUrl.indexOf("test");
		this.baseAssetUrl = assetUrl.slice(0, assetUrlIndex);
	}

	initializeElements() {
		this.imgObj = this.container.querySelector(
			`#flag-placeholder-${this.containerId}`
		);
		this.canvas.set("side", "left");
	}

	setupEventListeners() {
		// Store bound handlers for cleanup
		this.boundHandlers.set('resize', debounce(() => this.resizeCanvas(), 250));
		window.addEventListener('resize', this.boundHandlers.get('resize'));
        new ResizeObserver(debounce(() => this.resizeCanvas(), 250)).observe(this.outerContainer);

		// Name input listener with cleanup tracking
		if (this.nameInput) {
			const debouncedNameUpdate = debounce((value) => {
				if (this.riderId) {
					this.riderId.set("text", value);
					this.syncWidth(this.riderId);
					this.canvas.requestRenderAll();
				}
			}, 150);
			
			this.boundHandlers.set('nameInput', (e) => debouncedNameUpdate(e.target.value));
			this.nameInput.addEventListener("input", this.boundHandlers.get('nameInput'));
		}

		// Setup flag mode radio buttons
		const flagModeInputs = this.container.querySelectorAll(
			'input[name="properties[_FlagMode]"]'
		);
		flagModeInputs.forEach((input) => {
			const handler = (e) => this.setFlagMode(e.target.value);
			this.boundHandlers.set(`flagMode-${input.value}`, handler);
			input.addEventListener("change", handler);
		});

		// Setup toggle side links
		const toggleLink = this.container.querySelector(
			".flags-container .toggle-side-link"
		);
		if (toggleLink) {
			const handler = (e) => {
				e.preventDefault();
				const flagMode = this.container.querySelector(
					'input[name="properties[_FlagMode]"]:checked'
				).value;
				if (flagMode === "single" || flagMode === "double") {
					this.toggleSide();
				}
			};
			this.boundHandlers.set('toggleSide', handler);
			toggleLink.addEventListener("click", handler);
		}
	}

	resizeCanvas() {
		const startTime = performance.now();
		console.log('Resizing canvas...');
		const containerElement = this.outerContainer;
		const canvasElement = this.container.querySelector(".canvas-container");
		const innerWidth = this.getInnerWidth(containerElement);
		if (this.canvas.width > innerWidth.withoutPadding) {
			const scale = Math.min(innerWidth.withoutPadding / this.canvas.width);
			//canvasElement.style.transform = `scale(${scale})`;
			containerElement.style.setProperty(
				"--canvas-scale",
				scale.toString().slice(0, 10)
			);
		} else {
			canvasElement.style.transform = "scale(1)";
            containerElement.style.setProperty(
				"--canvas-scale",
				1
			);
		}
		//containerElement.style.setProperty("--editor-opacity", "1");
		this.logPerformance('resizeCanvas', startTime);
	}
	getInnerWidth(element) {
		// Method 1: Using clientWidth (includes padding)
		const withPadding = element.clientWidth;

		// Method 2: Using getBoundingClientRect (excludes everything)
		const styles = getComputedStyle(element);
		const withoutPadding =
			element.getBoundingClientRect().width -
			parseFloat(styles.borderLeftWidth) - 
			parseFloat(styles.borderRightWidth) -
			parseFloat(styles.paddingLeft) -
			parseFloat(styles.paddingRight);

		return {
			withPadding,
			withoutPadding,
		};
	}



	async loadAndUse(font, offset = 0) {
		const startTime = performance.now();
		console.log(`Loading font: ${font}...`, offset);

		if (font === "Times New Roman") {

			this.riderId.set("fontFamily", font);
			this.syncWidth(this.riderId);
			this.canvas.requestRenderAll();
			return;
		}

		try {
			// Create a temporary span to verify font loading
			const tempSpan = document.createElement("span");
			tempSpan.style.fontFamily = font;
			tempSpan.style.opacity = "0";
			tempSpan.textContent = "Test";
			document.body.appendChild(tempSpan);

			// Load font with timeout
			const myfont = new FontFaceObserver(font);
			await Promise.race([
				myfont.load(null, 5000), // 5 second timeout
				new Promise((_, reject) =>
					setTimeout(() => reject(new Error("Font load timeout")), 5000)
				),
			]);

			// Clean up temp span
			document.body.removeChild(tempSpan);
            const top = Number(offset) + Number((this.canvas.height / 2));
			// Apply font and force refresh
			this.riderId.set({
				fontFamily: font,
                top,
				dirty: true, // Force fabric.js to update
			});

			this.syncWidth(this.riderId);
			this.canvas.requestRenderAll();

			// Additional force refresh for iOS
			setTimeout(() => {
				this.canvas.requestRenderAll();
			}, 100);
			this.logPerformance('loadAndUse', startTime);
		} catch (e) {
			console.error("Font loading failed:", font, e);
			// Fallback to default font
			this.riderId.set("fontFamily", "Arial");
			this.syncWidth(this.riderId);
			this.canvas.requestRenderAll();
		}
	}

	syncWidth(textObject) {
		const startTime = performance.now();
		console.log('Syncing text width...');
		if (!textObject) return;

		const revisedText = this.sanitizeText(textObject);
		this.updateTextObject(textObject, revisedText);

		// Calculate available width (default to full width if flags not initialized)
		let availableWidth = this.canvasWidth;
		// Only check flag states if originalSelects is initialized
		if (this.originalSelects?.flagLeft) {
			const hasLeftFlag = this.originalSelects.flagLeft?.value !== "none";
			const hasRightFlag = this.originalSelects.flagRight?.value !== "none";
			const padding = this.options.padding || 0;

			if (hasLeftFlag && hasRightFlag) {
				// Double flag mode - subtract space for both flags and padding
				availableWidth =
					this.canvasWidth -
					(this.options.flagWidth * 2 + this.options.flagX * 4 + padding * 2);
			} else if (hasLeftFlag || hasRightFlag) {
				// Single flag mode - subtract space for one flag and padding
				availableWidth =
					this.canvasWidth - 
					(this.options.flagWidth + this.options.flagX * 4 + padding * 2);
			} else {
				// No flags mode - add margins and padding on both sides
				availableWidth = this.canvasWidth - (this.options.flagX * 2 + padding * 2);
			}
		}

		// Reset scale and width constraints
		textObject.set({
			scaleX: 1,
			scaleY: 1,
		});

		// Force fabric to recalculate the width
		textObject.initDimensions();
		const unscaledWidth = textObject.width;

		// Calculate and apply scale if needed
		if (unscaledWidth > availableWidth) {
			const scale = availableWidth / unscaledWidth;
			textObject.set({
				scaleX: scale,
				scaleY: 1,
			});
		}

		textObject.setCoords();
		this.logPerformance('syncWidth', startTime);
	}

	sanitizeText(textObject) {
		const textOriginal = textObject.text;
		return textOriginal
			.replace(/(\r\n|\n|\r)/gm, " ")
			.replace(/[^a-zA-Z0-9åäöüÅÄÖÜßÀ-ÿ@#.,-_ ]/g, "");
	}

	updateTextObject(textObject, revisedText) {
		textObject.set({ text: revisedText });

		if (textObject.get("text") === "") {
			this.setPlaceholderText(textObject);
		}
	}

	setPlaceholderText(textObject) {
		textObject.set({
			text: this.options.translations["placeholder"],
			opacity: 1,
		});
	}

	adjustTextScale(textObject) {
		const leftFlagVisible = this.leftFlag && this.leftFlag.opacity !== 0;
		const rightFlagVisible = this.rightFlag && this.rightFlag.opacity !== 0;
		const flagMode =
			this.container.querySelector(
				'input[name="properties[_FlagMode]"]:checked'
			)?.value || "single";

		let maxWidth;
		if (flagMode === "double" && rightFlagVisible) {
			// For double flag mode, account for both flags
			const leftPadding = leftFlagVisible
				? this.options.flagWidth + this.options.flagX
				: 20;
			const rightPadding = rightFlagVisible
				? this.options.flagWidth + this.options.flagX
				: 20;
			maxWidth = this.canvas.width - (leftPadding + rightPadding);
		} else {
			// For single flag mode or when right flag is hidden
			const flagPadding = leftFlagVisible
				? this.options.flagWidth + this.options.flagX
				: 20;
			maxWidth = this.canvas.width - (flagPadding + 40); // Only add padding from the flag side plus a small margin
		}

		// Debug logs
		console.log("Flag mode:", flagMode);
		console.log("Max width:", maxWidth);
		console.log("Text width:", textObject.width);

		const scale = Math.min(maxWidth / textObject.width, 1);
		textObject.set("scaleX", scale);
	}

	handleThemeChange(event) {
		this.resetThemeDefaults();
		this.applyTheme(event.target.value);
		this.canvas.requestRenderAll();
	}

	resetThemeDefaults() {
		this.riderId.set({ opacity: 1 });
		this.container.dataset.theme = "";
	}

	applyTheme(theme) {
		const themeOption = this.themeOptions.find((opt) => opt.value === theme);
		if (themeOption) {
			this.rect.set(themeOption.settings.rect);
			this.riderId.set(themeOption.settings.text);
			this.container.dataset.theme = themeOption.settings.dataset;
            this.outerContainer.style.setProperty("--canvas-text-color", themeOption.settings.text.fill);
            this.outerContainer.style.setProperty("--canvas-background-color", themeOption.settings.rect.fill);
            console.log(this);
            if (this.backgroundColorInput) this.backgroundColorInput.value = themeOption.settings.rect.fill;
            if (this.textColorInput) this.textColorInput.value = themeOption.settings.text.fill;
            
			if (
				themeOption.settings.dataset.includes("on-white") ||
				themeOption.settings.dataset.includes("white-on-transparent")
			) {
				// console.log(this.outerContainer);
				//  this.outerContainer.classList.replace('color-scheme-2','color-scheme-1');
			} else {
				//  this.outerContainer.classList.replace('color-scheme-1','color-scheme-2');
			}
			this.themeInput.value = themeOption.settings.dataset;
		} else {
			console.warn(`Theme "${theme}" not found in options`);
			// Set default theme
			this.rect.set({ fill: "black" });
			this.riderId.set({ fill: "white" });
			this.container.dataset.theme = "";
		}
		this.canvas.requestRenderAll();
	}

	toggleSide() {
		// Get current values
		const leftValue = this.originalSelects.flagLeft.value;
		const rightValue = this.originalSelects.flagRight.value;

		// Swap values in original selects
		this.originalSelects.flagLeft.value = rightValue;
		this.originalSelects.flagRight.value = leftValue;

		// Update custom select displays including flag icons
		const updateSelectDisplay = (customSelect, value) => {
			const button = customSelect.querySelector(".disclosure__button");
			const buttonTextElement = button.querySelector(
				".disclosure__button-text"
			);
			const existingIcon = button.querySelector(".flag-icon");

			// Remove existing flag icon
			if (existingIcon) {
				existingIcon.remove();
			}

			// Get the option details
			const originalSelect =
				value === rightValue
					? this.originalSelects.flagLeft
					: this.originalSelects.flagRight;
			const option = originalSelect.querySelector(`option[value="${value}"]`);

			// Update text
			if (buttonTextElement && option) {
				buttonTextElement.textContent = option.textContent;
			}

			// Add new flag icon if value isn't "none"
			if (value !== "none" && option?.dataset.code) {
				const flagUrl = `${this.baseAssetUrl}${option.dataset.code}.svg`;
				const flagIcon = document.createElement("img");
				flagIcon.src = flagUrl;
				flagIcon.className = "flag-icon";
				flagIcon.alt = option.textContent;
				flagIcon.width = 20;
				flagIcon.height = 15;
				button.insertBefore(flagIcon, buttonTextElement);
			}

			// Update hidden input
			const hiddenInput = customSelect.querySelector('input[type="hidden"]');
			if (hiddenInput) {
				hiddenInput.value = value;
			}

			// Update checkmarks
			const links = customSelect.querySelectorAll(".disclosure__link");
			links.forEach((link) => {
				const checkmark = link.querySelector("span:first-child");
				if (checkmark) {
					checkmark.classList.toggle(
						"visibility-hidden",
						link.dataset.value !== value
					);
				}
			});
		};

		// Update both custom selects
		updateSelectDisplay(this.customSelects.flagLeft, rightValue);
		updateSelectDisplay(this.customSelects.flagRight, leftValue);

		// Update flag icon input with both flags if they exist
		if (this.flagUrlInput) {
			const leftCode = rightValue !== 'none' ? this.originalSelects.flagLeft.querySelector(`option[value="${rightValue}"]`)?.dataset.code : null;
			const rightCode = leftValue !== 'none' ? this.originalSelects.flagRight.querySelector(`option[value="${leftValue}"]`)?.dataset.code : null;
			
			if (leftCode && rightCode) {
				// Both flags mode
				this.flagUrlInput.value = `${leftCode},${rightCode}`;
			} else if (leftCode) {
				// Left flag only
				this.flagUrlInput.value = leftCode;
			} else if (rightCode) {
				// Right flag only
				this.flagUrlInput.value = rightCode;
			} else {
				// No flags
				this.flagUrlInput.value = '';
			}
		}

		// Calculate new text position and width
		let availableWidth = this.canvasWidth;
		let textLeft = this.canvasWidth / 2;

		const hasLeftFlag = rightValue && rightValue !== "none"; // Note: values are swapped
		const hasRightFlag = leftValue && leftValue !== "none"; // Note: values are swapped

		if (hasLeftFlag && hasRightFlag) {
			// Double flag mode
			availableWidth =
				this.canvasWidth -
				(this.options.flagWidth * 2 + this.options.flagX * 4);
			textLeft = this.canvasWidth / 2;
		} else if (hasLeftFlag || hasRightFlag) {
			// Single flag mode
			availableWidth =
				this.canvasWidth - (this.options.flagWidth + this.options.flagX * 2);
			textLeft = hasLeftFlag
				? (this.canvasWidth + this.options.flagWidth + this.options.flagX) / 2
				: (this.canvasWidth - this.options.flagWidth - this.options.flagX) / 2;
		}

		// Update flags
		if (rightValue && rightValue !== "none") {
			this.updateFlag(rightValue, "left");
		} else {
			this.leftFlag?.set({ opacity: 0, visible: false });
		}

		if (leftValue && leftValue !== "none") {
			this.updateFlag(leftValue, "right");
		} else {
			this.rightFlag?.set({ opacity: 0, visible: false });
		}

		// Update text position and width
		if (this.riderId) {
			this.riderId.set({
				left: textLeft,
				originX: "center",
				maxWidth: availableWidth,
			});

			// Force text width update
			const currentText = this.riderId.text;
			this.riderId.set("text", currentText);
			this.syncWidth(this.riderId);
		}

		this.canvas.renderAll();
	}

	async updateFlag(flagName, side = "left") {
		const startTime = performance.now();
		console.log(`Updating flag: ${flagName} on ${side} side...`);
		
		// Validate flag selection
		if (!this.validateFlagSelection(flagName, side)) {
			console.warn('Flag validation failed:', {flagName, side});
			return;
		}

		const flagCode = this.getFlagCode(side, flagName);
		if (!flagCode || flagCode === 'none') {
			const flag = side === "left" ? this.leftFlag : this.rightFlag;
			if (flag) {
				flag.set({ opacity: 0, visible: false });
				this.canvas.renderAll();
			}

			// Update flag icon input
			if (this.flagUrlInput) {
				const otherSide = side === "left" ? "right" : "left";
				const otherValue = this.originalSelects[`flag${otherSide === "right" ? "Right" : "Left"}`]?.value;
				const otherCode = otherValue && otherValue !== 'none' ? 
					this.getFlagCode(otherSide, otherValue) : null;

				// If other side has a flag, keep it, otherwise clear
				this.flagUrlInput.value = otherCode || '';
			}
			return;
		}

		// Calculate flag position
		const verticalCenter = (this.canvasHeight - (this.options.flagHeight || 90)) / 2;
		const flagLeft = side === "left" 
			? this.contentLeft 
			: this.contentRight - (this.options.flagWidth || 120);

		const flag = side === "left" ? this.leftFlag : this.rightFlag;

		try {
			// Show loading state
			if (flag) {
				flag.set({ opacity: 0.5 });
				this.canvas.requestRenderAll();
			}

			// Load the flag (will use cache if available)
			await this.preloadFlag(flagCode);
			const finalImg = this.preloadedFlags.get(flagCode);

			if (finalImg && flag) {
				flag.set({
					opacity: 1,
					visible: true,
					left: flagLeft,
					top: verticalCenter,
					originX: "left",
					width: this.options.flagWidth || 120,
					height: this.options.flagHeight || 90
				});

				// Set the image with a small delay to ensure proper rendering in Safari
				setTimeout(() => {
					flag.setElement(finalImg);
					// Update text position based on new flag configuration
					const mode = this.determineMode();
					this.setFlagMode(mode);
					this.canvas.requestRenderAll();
				}, 0);

				// Update flag icon input
				if (this.flagUrlInput) {
					const otherSide = side === "left" ? "right" : "left";
					const otherValue = this.originalSelects[`flag${otherSide === "right" ? "Right" : "Left"}`]?.value;
					const otherCode = otherValue && otherValue !== 'none' ? 
						this.getFlagCode(otherSide, otherValue) : null;

					if (otherCode) {
						// Both flags mode - order them correctly
						const codes = side === "left" ? 
							[flagCode, otherCode] : 
							[otherCode, flagCode];
						this.flagUrlInput.value = codes.join(',');
					} else {
						// Single flag mode
						this.flagUrlInput.value = flagCode;
					}
				}
			}
		} catch (err) {
			console.error('Error updating flag:', err);
			this.handleFlagLoadError(err);
			// Reset opacity on error
			if (flag) {
				flag.set({ opacity: 1 });
				this.canvas.requestRenderAll();
			}
		}
	}

	validateFlagSelection(flagName, side) {
		const select =
			this.originalSelects[side === "left" ? "flagLeft" : "flagRight"];
		if (!select) {
			console.warn("Flag select not found:", side);
			return false;
		}

		const option = select.querySelector(`option[value="${flagName}"]`);
		if (!option) {
			console.warn("Flag not found:", flagName);
			return false;
		}
		return true;
	}

	getFlagCode(side, flagName) {
		const select =
			this.originalSelects[side === "left" ? "flagLeft" : "flagRight"];
		if (!select) return null;

		const option = select.querySelector(
			`option[value="${flagName.toLowerCase()}"]`
		);
		if (option) {
			return option.dataset.code;
		}
		return null;
	}

	async loadCountryFlags() {
		try {
			const editorIcons = document.getElementById("editor-icons");
			if (!editorIcons) throw new Error("Editor icons data not found");

			const categories = JSON.parse(editorIcons.textContent).categories;

			// Create flags row container
			const flagsContainer = this.container.querySelector(".flags-container");
			const flagsRow = document.createElement("div");
			flagsRow.className = "flags-row";
			flagsContainer.appendChild(flagsRow);

			// Create field containers first
			const leftFieldContainer = document.createElement("div");
			leftFieldContainer.className =
				"field flag-select-container left-flag-container";

			const rightFieldContainer = document.createElement("div");
			rightFieldContainer.className =
				"field flag-select-container right-flag-container";

			// Create switch button
			const switchButton = document.createElement("button");
			switchButton.className = "switch-side-button";
			switchButton.innerHTML = `
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="M8 3L4 7L8 11"/>
					<path d="M4 7H20"/>
					<path d="M16 21L20 17L16 13"/>
					<path d="M20 17H4"/>
				</svg>
			`;
			switchButton.setAttribute(
				"title",
				this.options.translations["switch-sides"]
			);
			switchButton.addEventListener("click", () => this.toggleSide());

			// Add containers and switch button to row
			flagsRow.appendChild(leftFieldContainer);
			flagsRow.appendChild(switchButton);
			flagsRow.appendChild(rightFieldContainer);

			// Initialize selects
			this.originalSelects = {
				flagLeft: this.createAndPlaceFlagSelect("left"),
				flagRight: this.createAndPlaceFlagSelect("right"),
			};

			// Add selects to their field containers
			leftFieldContainer.appendChild(this.originalSelects.flagLeft);
			rightFieldContainer.appendChild(this.originalSelects.flagRight);

			// Add labels to field containers
			const leftLabel = document.createElement("label");
			leftLabel.className = "field__label";
			leftLabel.htmlFor = this.originalSelects.flagLeft.id;
			leftLabel.textContent = this.options.translations['left-flag']; 
			leftFieldContainer.appendChild(leftLabel);

			const rightLabel = document.createElement("label");
			rightLabel.className = "field__label";
			rightLabel.htmlFor = this.originalSelects.flagRight.id;
			rightLabel.textContent = this.options.translations['right-flag']; 
			rightFieldContainer.appendChild(rightLabel);

			// Load categories into both selects
			await Promise.all([
				this.populateFlagSelect(this.originalSelects.flagLeft, categories),
				this.populateFlagSelect(this.originalSelects.flagRight, categories),
			]);

			// Convert to custom selects
			this.customSelects = {
				flagLeft: this.createCustomSelect(this.originalSelects.flagLeft, {
					onChange: (value) => {
						this.updateFlag(value, "left");
						const mode = this.determineMode();
						this.setFlagMode(mode);
					},
					showIcon: true,
					customClass: "flag-select",
					dataSide: "left",
				}),
				flagRight: this.createCustomSelect(this.originalSelects.flagRight, {
					onChange: (value) => {
						this.updateFlag(value, "right");
						const mode = this.determineMode();
						this.setFlagMode(mode);
					},
					showIcon: true,
					customClass: "flag-select",
					dataSide: "right",
				}),
			};

			// Move the custom selects into their respective field containers
			leftFieldContainer.appendChild(this.customSelects.flagLeft);
			rightFieldContainer.appendChild(this.customSelects.flagRight);

			// Initialize the displays with correct values
			if (this.options.preloadedFlag) {
				// Update left select with preloaded flag
				this.updateCustomSelectDisplay(
					this.customSelects.flagLeft,
					this.options.preloadedFlag.toLowerCase()
				);
				// Set right select to "No Flag"
				this.updateCustomSelectDisplay(this.customSelects.flagRight, "none");

				// Update the canvas with the preloaded flag
				await this.updateFlag(this.options.preloadedFlag, "left");
				const mode = this.determineMode();
				this.setFlagMode(mode);

				// Force width sync after everything is loaded
				if (this.riderId) {
					setTimeout(() => {
						this.syncWidth(this.riderId);
						this.canvas.renderAll();
					}, 100);
				}
			}

			// Setup flag preloading after everything is initialized
			this.setupFlagPreloading();

		} catch (error) {
			console.error("Error loading flags:", error);
			this.handleFlagLoadError(error);
		}
	}

	async populateFlagSelect(select, categories) {
		for (const category of categories) {
			const optgroup = document.createElement("optgroup");
			optgroup.label = category.name;

			category.json.icons.forEach((icon) => {
				const option = document.createElement("option");
				option.value = icon.name.toLowerCase();
				option.textContent = icon.name;
				option.dataset.code = icon.code;
                

				// Only select preloaded flag for left dropdown if it exists
				if (
					this.options.preloadedFlag &&
					icon.name.toLowerCase() ===
						this.options.preloadedFlag.toLowerCase() &&
					select.id === "flags"
				) {
					option.selected = true;
				}

				optgroup.appendChild(option);
			});

			select.appendChild(optgroup);
		}

		// Set "No Flag" as selected for right dropdown by default
		if (select.id === "flags-right") {
			const noneOption = select.querySelector('option[value="none"]');
			if (noneOption) {
				noneOption.selected = true;
			}
		}
	}

	createAndPlaceFlagSelect(side) {
		const select = document.createElement("select");
		select.id = `flags${side === "right" ? "-right" : ""}`;
		select.className = "flag-select";
		select.setAttribute("data-side", side);

		// Add default "no flag" option
		const noneOption = document.createElement("option");
		noneOption.value = "none";
		noneOption.textContent = this.options.translations["no-flag"];
		noneOption.dataset.code = "none";
		select.appendChild(noneOption);

		return select;
	}

	handleFlagLoadError(error) {
		console.error("Error loading country flags:", error);
		this.container.classList.add("flag-load-error");
		// Implement user feedback mechanism
	}

	afterRender() {
		const startTime = performance.now();
		console.log('After render processing...');
		if (!this.riderId) return;

		const currentText = this.riderId.text;
		this.pauseRenderListener();

		// Always sync width on render with a slight delay to ensure proper calculations
		setTimeout(() => {
			this.syncWidth(this.riderId);

			// Update text and opacity
			this.riderId.set({
				text: currentText,
				opacity: 1,
			});

			// Update PNG hash
			this.updatePngHash();

			this.resumeRenderListener();
		}, 0);
		this.logPerformance('afterRender', startTime);
	}

	pauseRenderListener() {
		this.canvas.off("after:render");
	}

	performRenderUpdates(currentText) {
		this.riderId.set({
			text: currentText,
			opacity: 1,
		});
	}

	updatePngHash() {
		const pngHash = this.canvas.toDataURL({ multiplier: 1 });
		this.pngHashInput.value = pngHash;
        

	}

	resumeRenderListener() {
		setTimeout(() => {
			this.canvas.on("after:render", () => this.afterRender());
		}, 200);
	}

	updateName(name) {
		this.riderId.set("text", name);
		this.syncWidth(this.riderId);
		this.canvas.renderAll();
	}

	// Method to get the current name value
	getName() {
		return this.nameInput.value;
	}

	// Method to set the name value
	setName(name) {
		this.nameInput.value = name;
		this.updateName(name);
	}

	setupFlags() {
		// Create a placeholder image element
		this.imgObj = new Image();
		this.imgObj.crossOrigin = "anonymous";

		const flagWidth = this.options.flagWidth || 120;
		const flagHeight = this.options.flagHeight || 90;
		const flagX = this.options.flagX || 20;
		const flagY = this.options.flagY || 20;

		// Create a blank canvas for initial flag setup
		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = flagWidth;
		tempCanvas.height = flagHeight;
		const ctx = tempCanvas.getContext('2d');
		ctx.fillStyle = 'transparent';
		ctx.fillRect(0, 0, flagWidth, flagHeight);
		
		const blankImage = new Image();
		blankImage.src = tempCanvas.toDataURL();

		// Create left flag with proper dimensions
		this.leftFlag = new fabric.Image(blankImage, {
			left: flagX,
			top: (this.canvasHeight - flagHeight) / 2,
			width: flagWidth,
			height: flagHeight,
			selectable: false,
			visible: false,
			crossOrigin: 'anonymous'
		});
		this.canvas.add(this.leftFlag);

		// Create right flag with proper dimensions
		this.rightFlag = new fabric.Image(blankImage, {
			left: this.canvasWidth - flagX - flagWidth,
			top: (this.canvasHeight - flagHeight) / 2,
			width: flagWidth,
			height: flagHeight,
			selectable: false,
			visible: false,
			crossOrigin: 'anonymous'
		});
		this.canvas.add(this.rightFlag);
	}

	setupText() {
		const textLeft =
			this.options.flagMode === "double"
				? this.canvasWidth / 2
				: (this.canvasWidth + this.options.flagWidth + this.options.flagX) / 2;

		this.riderId = new fabric.IText("", {
			lockMovementY: true,
			lockMovementX: true,
			top: this.canvasHeight / 2,
			left: textLeft,
			originX: "center",
			originY: "center",
			fontSize: 80,
			selectable: false,
			fill: this.options.textColor,
			hasControls: false,
		});
		this.canvas.add(this.riderId);

		// Add initial width sync
		this.syncWidth(this.riderId);
	}

	setFlagMode(mode) {
		const startTime = performance.now();
		console.log(`Setting flag mode: ${mode}...`);
		
		// Get current flag values
		const leftValue = this.originalSelects.flagLeft?.value;
		const rightValue = this.originalSelects.flagRight?.value;

		const hasLeftFlag = leftValue && leftValue !== "none";
		const hasRightFlag = rightValue && rightValue !== "none";

		// Calculate vertical center position
		const verticalCenter = (this.canvasHeight - this.options.flagHeight) / 2;

		if (hasLeftFlag && hasRightFlag) {
			// Double flag mode
			this.leftFlag?.set({
				opacity: 1,
				visible: true,
				left: this.contentLeft,
				top: verticalCenter
			});

			this.rightFlag?.set({
				opacity: 1,
				visible: true,
				left: this.contentRight - this.options.flagWidth,
				top: verticalCenter
			});

			// Center text between flags
			if (this.riderId) {
				const textLeft = this.contentLeft + (this.contentMaxWidth / 2);
				this.riderId.set({
					left: textLeft,
					originX: "center",
					maxWidth: this.contentMaxWidth - (this.options.flagWidth * 2 + this.options.flagX * 2)
				});
			}
		} else if (hasLeftFlag || hasRightFlag) {
			// Single flag mode
			if (hasLeftFlag) {
				this.leftFlag?.set({
					opacity: 1,
					visible: true,
					left: this.contentLeft,
					top: verticalCenter
				});
				this.rightFlag?.set({
					opacity: 0,
					visible: false
				});

				// Position text to the right of left flag
				if (this.riderId) {
					const textLeft = this.contentLeft + (this.contentMaxWidth + this.options.flagWidth) / 2;
					this.riderId.set({
						left: textLeft,
						originX: "center",
						maxWidth: this.contentMaxWidth - (this.options.flagWidth + this.options.flagX)
					});
				}
			} else {
				this.rightFlag?.set({
					opacity: 1,
					visible: true,
					left: this.contentRight - this.options.flagWidth,
					top: verticalCenter
				});
				this.leftFlag?.set({
					opacity: 0,
					visible: false
				});

				// Position text to the left of right flag
				if (this.riderId) {
					const textLeft = this.contentLeft + (this.contentMaxWidth - this.options.flagWidth) / 2;
					this.riderId.set({
						left: textLeft,
						originX: "center",
						maxWidth: this.contentMaxWidth - (this.options.flagWidth + this.options.flagX)
					});
				}
			}
		} else {
			// No flags mode
			this.leftFlag?.set({
				opacity: 0,
				visible: false
			});
			this.rightFlag?.set({
				opacity: 0,
				visible: false
			});

			// Center text
			if (this.riderId) {
				const textLeft = this.contentLeft + (this.contentMaxWidth / 2);
				this.riderId.set({
					left: textLeft,
					originX: "center",
					maxWidth: this.contentMaxWidth
				});
			}
		}

		// Update text width constraints
		if (this.riderId) {
			this.syncWidth(this.riderId);
		}

		this.canvas.renderAll();
		this.logPerformance('setFlagMode', startTime);
	}

	createCustomSelect(originalSelect, options = {}) {
		const { onChange = () => {}, showIcon = false, customClass = "", showColorSwatch = false } = options;
		
		const customSelect = document.createElement('div');
		customSelect.className = `disclosure ${customClass}`;
		
		// Create the button
		const button = document.createElement("button");
		button.className = "disclosure__button link link--text caption-large";
		button.setAttribute("aria-expanded", "false");
		button.setAttribute("type", "button");

		// Get initially selected option
		const selectedOption = originalSelect.options[originalSelect.selectedIndex];

		// Create button content with separate spans
		const buttonText = document.createElement("span");
		buttonText.className = "disclosure__button-text";
		buttonText.textContent = selectedOption
			? selectedOption.text
			: "Select option";

		const buttonCaret = document.createElement("span");
		buttonCaret.className = "icon-caret";
		buttonCaret.innerHTML = '<!-- inline_asset_content liquid filter is not supported -->';

		// Append text and caret to button
		button.appendChild(buttonText);
		button.appendChild(buttonCaret);

		// Create the list wrapper
		const listWrapper = document.createElement("div");
		listWrapper.className = "disclosure__list-wrapper";
		listWrapper.hidden = true;

		// Create the list
		const list = document.createElement("div");
		list.className = "disclosure__list";
		const itemsList = document.createElement("ul");
		itemsList.className = "list-unstyled";
		itemsList.setAttribute("role", "list");

		// Convert options to list items
		Array.from(originalSelect.children).forEach((child) => {
			if (child.tagName === "OPTGROUP") {
				// Handle optgroup
				const groupLabel = document.createElement("div");
				groupLabel.className = "disclosure__group-label caption-large";
				groupLabel.textContent = child.label;
				itemsList.appendChild(groupLabel);

				Array.from(child.children).forEach((option) => {
					itemsList.appendChild(this.createSelectListItem(option, showIcon));
				});
			} else {
				// Handle direct option
				itemsList.appendChild(this.createSelectListItem(child, showIcon));
			}
		});

		// Assemble the components
		list.appendChild(itemsList);
		listWrapper.appendChild(list);
		customSelect.appendChild(button);
		customSelect.appendChild(listWrapper);

		// Add overlay
		const overlay = document.createElement("div");
		overlay.className = "disclosure__overlay";
		customSelect.appendChild(overlay);

		// Setup event listeners with modified onChange handling
		const handleChange = (value, text) => {
			// Update the original select value
			originalSelect.value = value;

			// Call the provided onChange handler
			onChange(value, text);

			// Get the current mode after the change
			const mode = this.determineMode();

			// Update flags based on the side
			const side = originalSelect.getAttribute("data-side");
			if (side) {
				if (value === "none") {
					// Hide the flag on this side
					const flag = side === "left" ? this.leftFlag : this.rightFlag;
					if (flag) {
						flag.set({ opacity: 0, visible: false });
					}
				} else {
					// Show and update the flag
					this.updateFlag(value, side);
				}

				// Calculate new text position and width
				let availableWidth = this.canvasWidth;
				let textLeft = this.canvasWidth / 2;

				const hasLeftFlag = this.originalSelects.flagLeft?.value !== "none";
				const hasRightFlag = this.originalSelects.flagRight?.value !== "none";

				if (hasLeftFlag && hasRightFlag) {
					// Double flag mode
					availableWidth -= this.options.flagWidth * 2 + this.options.flagX * 4;
					textLeft = this.canvasWidth / 2;
				} else if (hasLeftFlag || hasRightFlag) {
					// Single flag mode
					availableWidth -= this.options.flagWidth + this.options.flagX * 2;
					textLeft = hasLeftFlag
						? (this.canvasWidth + this.options.flagWidth + this.options.flagX) /
						  2
						: (this.canvasWidth - this.options.flagWidth - this.options.flagX) /
						  2;
				}

				// Update text position and width
				if (this.riderId) {
					this.riderId.set({
						left: textLeft,
						originX: "center",
						maxWidth: availableWidth,
					});

					// Force text width update
					const currentText = this.riderId.text;
					this.riderId.set("text", currentText);
					this.syncWidth(this.riderId);
				}
			}

			// Update canvas
			this.canvas.renderAll();
		};

		// Setup the select events with the new handler
		this.setupCustomSelectEvents(customSelect, handleChange);

		// Replace original select within its container
		const container = originalSelect.parentElement;
		if (container) {
			container.appendChild(customSelect);
			container.removeChild(originalSelect);
		}

		// Debug log
		console.log("Created custom select:", {
			hasButtonText: !!customSelect.querySelector(".disclosure__button-text"),
			buttonText: customSelect.querySelector(".disclosure__button-text")
				?.textContent,
			value: originalSelect.value,
		});

		// Add search input for flag selects
		let searchInput;
		if (customClass.includes('flag-select')) {
			const { searchContainer, searchInput: input } = this.createFlagSearch();
			searchInput = input;
			listWrapper.insertBefore(searchContainer, list);
			
			// Add search functionality
			searchInput.addEventListener('input', (e) => {
				const searchTerm = e.target.value.toLowerCase();
				const items = itemsList.querySelectorAll('li');
				
				items.forEach(item => {
					const link = item.querySelector('.disclosure__link');
					if (!link) return; // Skip if not a flag item
					
					const text = link.querySelector('.disclosure__label')?.textContent.toLowerCase();
					if (text === this.options.translations["no-flag"].toLowerCase() || 
						text.includes(searchTerm)) {
						item.style.display = '';
					} else {
						item.style.display = 'none';
					}
				});
			});
			
			// Prevent dropdown from closing when typing
			searchInput.addEventListener('click', (e) => {
				e.stopPropagation();
			});
			
			// Focus search input when dropdown opens
			button.addEventListener('click', () => {
				if (button.getAttribute('aria-expanded') === 'false') {
					setTimeout(() => searchInput.focus(), 0);
				}
			});
		}

		return customSelect;
	}

	createSelectListItem(option, showIcon = false, showColorSwatch = false) {
		// Store reference to 'this' for use in event handlers
		const self = this;
		
		// If this is the custom color option, create color picker container instead of regular item
		if (option.value === 'custom' && this.riderId && this.rect) {
			const container = document.createElement('li');
			container.className = 'disclosure__item disclosure__color-pickers';
			
			// Updated function to handle transparent backgrounds
			const updateThemeState = () => {
				const themeSelect = self.container.querySelector('.theme-select');
				if (themeSelect) {
					const buttonText = themeSelect.querySelector('.disclosure__button-text');
                    
					if (buttonText) {
						buttonText.textContent = this.options.translations["theme-custom"];
					}
					
					// Determine theme based on background
					const isTransparent = this.rect.get('fill') === 'transparent';
					const themeValue = isTransparent ? 'custom-transparent' : 'custom';
					
					// Update dataset and inputs
					this.container.dataset.theme = themeValue;
					this.themeInput.value = themeValue;
					
					// Update original select value
					if (this.originalSelects.theme) {
						this.originalSelects.theme.value = 'custom';
					}
				}
			};
			
			// Create text color picker
			const textColorContainer = document.createElement('div');
			textColorContainer.className = 'dropdown-color-picker';
			const textColorLabel = document.createElement('span');
			textColorLabel.className = 'dropdown-color-label';
			textColorLabel.textContent = this.options.translations["custom-text-color"];
			const textColorGrid = this.createColorGrid((color) => {
				if (self.riderId) {
					self.riderId.set('fill', color);
					this.outerContainer.style.setProperty("--canvas-text-color", color);                
                    if (this.textColorInput) this.textColorInput.value = color;
					updateThemeState();
					self.canvas.requestRenderAll();
				}
			}, this.riderId ? this.riderId.get('fill') : '#FFFFFF');
			textColorContainer.appendChild(textColorLabel);
			textColorContainer.appendChild(textColorGrid);

			// Create background color picker
			const bgColorContainer = document.createElement('div');
			bgColorContainer.className = 'dropdown-color-picker';
			const bgColorLabel = document.createElement('span');
			bgColorLabel.className = 'dropdown-color-label';
			bgColorLabel.textContent = this.options.translations["custom-background-color"];
			const bgColorGrid = this.createColorGrid((color) => {
				if (self.rect) {
					self.rect.set('fill', color);
					this.outerContainer.style.setProperty("--canvas-background-color", color);
                    if (this.backgroundColorInput) this.backgroundColorInput.value = color;
					updateThemeState();
					self.canvas.requestRenderAll();
				}
			}, this.rect ? this.rect.get('fill') : '#000000', true);
			bgColorContainer.appendChild(bgColorLabel);
			bgColorContainer.appendChild(bgColorGrid);

			// Add both pickers to container
			container.appendChild(textColorContainer);
			container.appendChild(bgColorContainer);

			// Prevent dropdown from closing when clicking color buttons
			container.addEventListener('click', (e) => {
				if (e.target.closest('.color-button')) {
					e.stopPropagation();
				}
			});

			return container;
		}

		// Regular list item creation for other options
		const li = document.createElement('li');
		li.className = 'disclosure__item';
		li.tabIndex = -1;

		const a = document.createElement('a');
		a.className = 'link link--text disclosure__link caption-large focus-inset';
		a.href = '#';
		a.dataset.value = option.value;
        if (option.dataset.offset) {
          a.dataset.offset = option.dataset.offset;
        }

		const isSelected = option.selected;

		// Create the checkmark span
		const checkmark = document.createElement('span');
		checkmark.className = isSelected ? '' : 'visibility-hidden';
		checkmark.innerHTML = showIcon ? '<!-- inline_asset_content liquid filter is not supported -->' : '';
		a.appendChild(checkmark);

		// Add label
		const label = document.createElement('span');
		label.className = 'disclosure__label';
		if (!showColorSwatch) {
			label.style.setProperty('--value',"\"" + option.value + "\"");
		}
		label.textContent = option.text;
		a.appendChild(label);

		li.appendChild(a);
		return li;
	}

	setupCustomSelectEvents(customSelect, onChange) {
		const button = customSelect.querySelector(".disclosure__button");
		const listWrapper = customSelect.querySelector(".disclosure__list-wrapper");
		const links = customSelect.querySelectorAll(".disclosure__link");

		// Find the corresponding hidden input based on the select's name
		const selectName = customSelect.classList.contains('flag-select') ? 
			'properties[_Icon]' : 
			customSelect.classList.contains('font-select') ? 
				'properties[_Font]' : 
				customSelect.classList.contains('theme-select') ? 
					'properties[_Theme]' : null;

		const hiddenInput = selectName ? this.container.querySelector(`input[name="${selectName}"]`) : null;

		// Toggle dropdown
		button.addEventListener("click", () => {
			const isExpanded = button.getAttribute("aria-expanded") === "true";
			button.setAttribute("aria-expanded", !isExpanded);
			listWrapper.hidden = isExpanded;
            const searchInput = listWrapper.querySelector('.disclosure__search-input');
         
            if (searchInput) {
				// Use setTimeout to ensure the dropdown is fully rendered
				   setTimeout(() => searchInput.focus(), 0);
			}
		});

		// Handle item selection
		links.forEach((link) => {
			link.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();

				const value = link.dataset.value;
				const labelElement = link.querySelector(".disclosure__label");

				if (!labelElement) {
					console.warn("Label element not found in link:", link);
					return;
				}

				const text = labelElement.textContent;

				// Update button text and flag icon
				const buttonTextElement = button.querySelector(
					".disclosure__button-text"
				);
				const existingIcon = button.querySelector(".flag-icon");
				if (existingIcon) {
					existingIcon.remove();
				}

				// Add flag icon if this is a flag select and not "none"
				if (
					customSelect.classList.contains("flag-select") &&
					value !== "none"
				) {
					const side = customSelect.getAttribute("data-side");
					const originalSelect =
						this.originalSelects[`flag${side === "right" ? "Right" : "Left"}`];
					const option = originalSelect.querySelector(
						`option[value="${value}"]`
					);

					if (option?.dataset.code) {
						const flagUrl = `${this.baseAssetUrl}${option.dataset.code}.svg`;
						const flagIcon = document.createElement("img");
						flagIcon.src = flagUrl;
						flagIcon.className = "flag-icon";
						flagIcon.alt = text;
						flagIcon.width = 20;
						flagIcon.height = 15;
						button.insertBefore(flagIcon, buttonTextElement);
					}
				}

				// Update button text
				if (buttonTextElement) {
					buttonTextElement.textContent = text;
				} else {
					const newSpan = document.createElement("span");
					newSpan.className = "disclosure__button-text";
					newSpan.textContent = text;
					button.insertBefore(newSpan, button.firstChild);
				}

				// Update hidden input if found
				if (hiddenInput) {
					hiddenInput.value = value;
				}

				// Update checkmarks
				const checkmarks = customSelect.querySelectorAll(
					".disclosure__link > span:first-child"
				);
				checkmarks.forEach((span) => span.classList.add("visibility-hidden"));
				const selectedCheckmark = link.querySelector("span:first-child");
				if (selectedCheckmark) {
					selectedCheckmark.classList.remove("visibility-hidden");
				}

				// Close dropdown
				listWrapper.hidden = true;
				button.setAttribute("aria-expanded", "false");

				// Trigger onChange callback
				onChange(value, text);
			});
		});

		// Close on outside click
		document.addEventListener("click", (e) => {
			if (!customSelect.contains(e.target)) {
				listWrapper.hidden = true;
				button.setAttribute("aria-expanded", "false");
			}
		});

		// Handle keyboard navigation
		customSelect.addEventListener("keyup", (e) => {
			if (e.code === "Escape") {
				listWrapper.hidden = true;
				button.setAttribute("aria-expanded", "false");
				button.focus();
			}
		});
	}

	// Add this new method to handle flag updates when mode changes
	updateFlagsForMode(mode) {
		if (mode === "single") {
			// Get current left flag value
			const leftValue = this.originalSelects.flagLeft?.value;
			if (leftValue) {
				// Make sure left flag is visible first
				this.leftFlag?.set({
					opacity: 1,
					visible: true,
					left: this.contentLeft,
					originX: "left",
				});

				// Update the flag image
				this.updateFlag(leftValue, "left");
			}

			// Hide right flag
			if (this.rightFlag) {
				this.rightFlag.set({
					opacity: 0,
					visible: false,
				});
			}
		} else if (mode === "double") {
			// Get both flag values
			const leftValue = this.originalSelects.flagLeft?.value;
			const rightValue = this.originalSelects.flagRight?.value;

			// Update both flags
			if (leftValue) {
				// Show left flag first
				this.leftFlag?.set({
					opacity: 1,
					visible: true,
					left: this.contentLeft,
					originX: "left",
				});
				this.updateFlag(leftValue, "left");
			}

			if (rightValue) {
				// Show right flag first
				this.rightFlag?.set({
					opacity: 1,
					visible: true,
					left: this.contentRight - this.options.flagWidth,
					originX: "left",
				});
				this.updateFlag(rightValue, "right");
			}
		} else if (mode === "none") {
			// Hide both flags
			if (this.leftFlag) {
				this.leftFlag.set({ opacity: 0, visible: false });
			}
			if (this.rightFlag) {
				this.rightFlag.set({ opacity: 0, visible: false });
			}
		}

		// Make sure text width is updated
		if (this.riderId) {
			this.syncWidth(this.riderId);
		}

		this.canvas.renderAll();
	}

	// Add new method to determine flag mode based on dropdown values
	determineMode() {
		if (!this.originalSelects) return "single";

		const leftValue = this.originalSelects.flagLeft?.value;
		const rightValue = this.originalSelects.flagRight?.value;

		const hasLeftFlag = leftValue && leftValue !== "none";
		const hasRightFlag = rightValue && rightValue !== "none";

		if (hasLeftFlag && hasRightFlag) {
			return "double";
		} else if (hasLeftFlag || hasRightFlag) {
			return "single";
		}
		return "none";
	}

	// Add method to determine active side for single mode
	determineActiveSide() {
		if (!this.originalSelects) return "left"; // Default side during initialization

		const leftValue = this.originalSelects.flagLeft?.value;
		const rightValue = this.originalSelects.flagRight?.value;

		if (leftValue && leftValue !== "none") return "left";
		if (rightValue && rightValue !== "none") return "right";
		return "left"; // default
	}

	// Add helper method to update custom select display
	updateCustomSelectDisplay(customSelect, value) {
		const button = customSelect.querySelector(".disclosure__button");
		const buttonTextElement = button.querySelector(".disclosure__button-text");
		const existingIcon = button.querySelector(".flag-icon");
		const option = this.originalSelects[
			customSelect === this.customSelects.flagLeft ? "flagLeft" : "flagRight"
		].querySelector(`option[value="${value}"]`);

		// Remove existing flag icon
		if (existingIcon) {
			existingIcon.remove();
		}

		// Update text
		if (buttonTextElement && option) {
			buttonTextElement.textContent = option.textContent;
		}

		// Add flag icon if this is a flag select and not "none"
		if (
			customSelect.classList.contains("flag-select") &&
			value !== "none" &&
			option?.dataset.code
		) {
			const flagUrl = `${this.baseAssetUrl}${option.dataset.code}.svg`;
			const flagIcon = document.createElement("img");
			flagIcon.src = flagUrl;
			flagIcon.className = "flag-icon";
			flagIcon.alt = option.textContent;
			flagIcon.width = 20;
			flagIcon.height = 15;
			button.insertBefore(flagIcon, buttonTextElement);
		}

		// Update hidden input
		const hiddenInput = customSelect.querySelector('input[type="hidden"]');
		if (hiddenInput) {
			hiddenInput.value = value;
		}

		// Update checkmarks
		const links = customSelect.querySelectorAll(".disclosure__link");
		links.forEach((link) => {
			const checkmark = link.querySelector("span:first-child");
			if (checkmark) {
				checkmark.classList.toggle(
					"visibility-hidden",
					link.dataset.value !== value
				);
			}
		});
	}

	initializeCanvas() {
		const startTime = performance.now();
		console.log('Initializing canvas...');
		this.createCanvas();
		this.canvas = new fabric.Canvas(this.canvasId);

		// Set canvas dimensions from options
		this.canvasWidth = this.options.canvasWidth || 660;
		this.canvasHeight = this.options.canvasHeight || 130;
		
		// Calculate content area dimensions
		this.contentPadding = (this.options.padding || 0) + (this.options.flagX || 20);
		this.contentMaxWidth = this.canvasWidth - (this.contentPadding * 2);
		this.contentLeft = this.contentPadding;
		this.contentRight = this.canvasWidth - this.contentPadding;

		// Set canvas dimensions
		this.canvas.setDimensions({
			width: this.canvasWidth,
			height: this.canvasHeight
		});

		// Add background rectangle with full canvas dimensions
		this.rect = new fabric.Rect({
			left: 0,
			top: 0,
			width: this.canvasWidth,
			height: this.canvasHeight,
			fill: this.options.backgroundColor || 'black',
			selectable: false
		});
		this.canvas.add(this.rect);

		// Initialize flags and text
		this.setupFlags();
		this.setupText();

		// Add after render callback
		this.canvas.on("after:render", () => this.afterRender());

		// Initial resize
		this.resizeCanvas();
		this.logPerformance('initializeCanvas', startTime);
	}

	setText(text) {
		if (this.riderId) {
			this.riderId.set("text", text);
			this.canvas.requestRenderAll();
		}
	}

	getFlagUrl() {
		return this.loadedFlagUrl;
	}

	createColorGrid(onChange, currentColor, isBackgroundPicker) {
		const grid = document.createElement('div');
		grid.className = 'colors-grid colors-grid--compact';
		
		 if (isBackgroundPicker) {
        const transparentButton = document.createElement('button');
        transparentButton.className = 'color-button color-button--transparent';
        transparentButton.setAttribute('data-color', 'transparent');
        transparentButton.style.background = 'repeating-conic-gradient(#808080 0% 25%, #fff 0% 50%) 50% / 10px 10px';
        if (currentColor === 'transparent') {
            transparentButton.classList.add('selected');
        }
        transparentButton.addEventListener('click', () => {
            grid.querySelectorAll('.color-button').forEach(btn => btn.classList.remove('selected'));
            transparentButton.classList.add('selected');
            onChange('transparent');
        });
        grid.appendChild(transparentButton);
    }
		
		this.options.colors.forEach(color => {
			const colorButton = document.createElement('button');
			colorButton.className = 'color-button';
			colorButton.style.backgroundColor = color;
			if (color === currentColor) {
				colorButton.classList.add('selected');
			}
			colorButton.addEventListener('click', () => {
				grid.querySelectorAll('.color-button').forEach(btn => btn.classList.remove('selected'));
				colorButton.classList.add('selected');
				onChange(color);
				// Update theme state after color change
				if (typeof this.updateThemeState === 'function') {
					this.updateThemeState();
				}
			});
			grid.appendChild(colorButton);
		});
		
		return grid;
	}

	// Add this method to create the search input
	createFlagSearch() {
		const searchContainer = document.createElement('div');
		searchContainer.className = 'disclosure__search field';
		
		const searchInput = document.createElement('input');
		searchInput.type = 'text';
		searchInput.className = 'disclosure__search-input field__input';
		searchInput.placeholder = this.options.translations["search-flags"];


        const searchLabel = document.createElement('label');
        searchLabel.className = 'field__label';
        searchLabel.innerText = this.options.translations["search-flags"];
		searchContainer.appendChild(searchInput);
      	searchContainer.appendChild(searchLabel);
		return { searchContainer, searchInput };
	}

	// Add updateThemeState as a class method
	updateThemeState() {
		const themeSelect = this.container.querySelector('.theme-select');
		if (themeSelect) {
			const buttonText = themeSelect.querySelector('.disclosure__button-text');
			if (buttonText) {
				buttonText.textContent = this.options.translations["theme-custom"];
			}
			
			// Determine theme based on background
			const isTransparent = this.rect.get('fill') === 'transparent';
			const themeValue = isTransparent ? 'custom-transparent' : 'custom';
			
			// Update dataset and inputs
			this.container.dataset.theme = themeValue;
			this.themeInput.value = themeValue;
			
			// Update original select value
			if (this.originalSelects.theme) {
				this.originalSelects.theme.value = 'custom';
			}
		}
	}

	async preloadFlag(flagCode) {
		if (!flagCode || flagCode === 'none') return;
		
		// If already loaded, return immediately
		if (this.preloadedFlags.has(flagCode)) {
			return;
		}

		// If already loading, return existing promise
		if (this.flagLoadingPromises.has(flagCode)) {
			return this.flagLoadingPromises.get(flagCode);
		}

		try {
			const loadingPromise = (async () => {
				const flagUrl = `${this.baseAssetUrl}${flagCode}.svg`;
				
				// First, fetch the SVG as a blob
				const response = await fetch(flagUrl, { 
					mode: 'cors',
					credentials: 'same-origin'
				});
				const blob = await response.blob();
				const blobUrl = URL.createObjectURL(blob);

				// Create and load the initial image
				const img = new Image();
				img.crossOrigin = 'anonymous';
				
				await new Promise((resolve, reject) => {
					img.onload = resolve;
					img.onerror = reject;
					img.src = blobUrl;
				});

				// Create canvas and draw the image
				const tempCanvas = document.createElement('canvas');
				const flagWidth = this.options.flagWidth || 120;
				const flagHeight = this.options.flagHeight || 90;
				tempCanvas.width = flagWidth;
				tempCanvas.height = flagHeight;
				const ctx = tempCanvas.getContext('2d');
				ctx.drawImage(img, 0, 0, flagWidth, flagHeight);

				// Create the final image
				const processedImg = new Image();
				processedImg.crossOrigin = 'anonymous';

				await new Promise((resolve, reject) => {
					processedImg.onload = () => {
						// Only store the image once it's fully loaded
						this.preloadedFlags.set(flagCode, processedImg);
						// Clean up the blob URL
						URL.revokeObjectURL(blobUrl);
						resolve();
					};
					processedImg.onerror = reject;
					processedImg.src = tempCanvas.toDataURL('image/png');
				});
			})();

			// Store the loading promise
			this.flagLoadingPromises.set(flagCode, loadingPromise);
			
			// Wait for loading to complete
			await loadingPromise;
			
			// Clean up the loading promise
			this.flagLoadingPromises.delete(flagCode);
			
		} catch (error) {
			console.warn(`Failed to preload flag: ${flagCode}`, error);
			// Clean up on error
			this.flagLoadingPromises.delete(flagCode);
			throw error;
		}
	}

	setupFlagPreloading() {
		// Setup intersection observer for visible flag options
		this.flagObserver = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					const link = entry.target;
					const option = link.closest('li')?.querySelector('.disclosure__link');
					if (option) {
						const flagCode = this.getFlagCodeFromOption(option.dataset.value);
						if (flagCode) {
							this.preloadFlag(flagCode);
						}
					}
				}
			});
		}, {
			root: null,
			rootMargin: '50px',
			threshold: 0.1
		});

		// Add hover preloading to flag options
		const setupHoverPreload = (select) => {
			select.querySelectorAll('.disclosure__link').forEach(link => {
				// Observe for visibility
				this.flagObserver.observe(link);

				// Preload on hover
				link.addEventListener('mouseenter', () => {
					const flagCode = this.getFlagCodeFromOption(link.dataset.value);
					if (flagCode) {
						this.preloadFlag(flagCode);
					}
				});
			});
		};

		// Setup preloading for both flag selects
		if (this.customSelects.flagLeft) {
			setupHoverPreload(this.customSelects.flagLeft);
		}
		if (this.customSelects.flagRight) {
			setupHoverPreload(this.customSelects.flagRight);
		}
	}

	getFlagCodeFromOption(value) {
		if (!value || value === 'none') return null;
		const option = this.originalSelects.flagLeft.querySelector(`option[value="${value}"]`) ||
					  this.originalSelects.flagRight.querySelector(`option[value="${value}"]`);
		return option?.dataset.code || null;
	}
}

function replaceTitle(str, svg) {
	const titleElement = document.querySelector(".product__title > *");
	const titleHtml = titleElement.innerHTML;
	console.log(svg.src, str);

	titleElement.innerHTML = titleHtml.replace(
		str,
		`<span style="--svg: url('${svg.src}')" class="title_country">${str}</span>`
	);
}
function getCountryNameByCode(code) {
	const data = JSON.parse(document.getElementById("editor-icons").textContent);
	const categories = data.categories;

	// Search through all categories and their icons
	for (const category of categories) {
		const icon = category.json.icons.find(
			(icon) => icon.code === code.toLowerCase()
		);
		if (icon) {
			return icon.name;
		}
	}

	return null;
}